import React, { useEffect, useRef, useState } from 'react';
import { TableRow, Dropdown as FilterDropdown } from 'semantic-ui-react';
import styled, { css } from 'styled-components';
import { COLORS, Row } from '@cuemath/comps';
import { Text, Dropdown, Button as WCButton, Checkbox } from '@cuemath/web-comps';

import { Table, Filters, TableCell } from 'comps';
import TransferStatusCards from 'comps/trasfer-status-cards';
import { BULK_TRANSFER_REASONS_OPTIONS } from 'constants/student-transfer';
import Search from 'modules/list/new-search-bar';
import { REGION_FILTER_OPTION } from 'constants/student-transfer';
import {
  reasonBasedWarningMessage,
  semanticDropdownDataFormat,
} from 'utils/student-transfer';

import {
  EditSlotIcon,
  EditSlotWrapper,
  HeaderCard,
  TableContainer,
  TransferTableRoot,
} from '../student-transfer/styles/transfer-info';
import { TransferListFooter } from './styles';
import {
  convertEpochDateToSTSlotTime,
  getDateOfRequest,
  getFilteredStudentTransferInfo,
  getSlotSelectorAddedList,
} from '../../utils/student-transfer';
import { TRANSFER_REASONS } from '../../constants/student-transfer';

const Button = styled(WCButton)`
  width: 400px;
  height: 48px;
`;

const DropdownWrapper = styled.div`
  position: relative;
  padding-top: 20px;
`;

const DropdonwWarningMessage = styled(Text)`
  padding: 0 16px;
`;

const StyledTableCell = styled(TableCell)`
  cursor: pointer;
  position: relative;
`;

const CheckBoxWrapper = styled(Row)`
  padding: 0;
  width: 100%;
  cursor: pointer;
  position: relative;
  z-index: 1;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `};
`;

const ArrowTooltip = styled(Text)`
  position: absolute;
  top: -3px;
  right: 110%;

  width: 93px;
  padding: 4px 8px;
  border-radius: 8px;
  background: ${COLORS.COLA};

  :after {
    content: '';
    position: absolute;
    top: 8px;
    right: -3px;
    transform: rotate(45deg);
    background: ${COLORS.COLA};
    width: 8px;
    height: 8px;
  }
`;

const TransferStudentList = ({ onEditTransferSlots, transferStudentAPIData }) => {
  const initalTransferStudentList = useRef();

  const [transferReason, setTransferReason] = useState('');
  const [selectAllStudents, setSelectAllStudents] = useState(false);
  const [transferStudentList, setTransferStudentList] = useState([]);
  const [hoverCheckSlot, setHoveredCheckSlot] = useState('');

  const [filterOptionsSelected, setFilterOptionsSelected] = useState({
    regionFilter: [],
    slotsFilter: [],
    searchKey: '',
  });

  const onRegionFilter = (_, data) => {
    const { value } = data || {};

    setFilterOptionsSelected(prev => ({ ...prev, regionFilter: value }));
  };

  const onStudentSelection = id => {
    const updatedStudentList = transferStudentList.map(student => {
      if (student.id === id || student.student_id === id) {
        return {
          ...student,
          selected: !student.selected,
        };
      }
      return student;
    });
    const studentWithSlots = updatedStudentList.filter(student => !student.editSlot);
    const didSelectAllStudents =
      studentWithSlots.filter(student => student.selected).length ===
      studentWithSlots.length;

    setTransferStudentList(updatedStudentList);

    if (didSelectAllStudents) {
      setSelectAllStudents(true);
    } else if (!didSelectAllStudents && selectAllStudents) {
      setSelectAllStudents(false);
    }
  };

  const onSelectAllStudents = () => {
    setSelectAllStudents(prev => {
      let updatedStudentList = null;

      if (prev) {
        updatedStudentList = transferStudentList.map(student => ({
          ...student,
          selected: false,
        }));
      } else {
        updatedStudentList = transferStudentList.map(student => ({
          ...student,
          selected: !!student.regularSlots,
        }));
      }
      setTransferStudentList(updatedStudentList);

      return !prev;
    });
  };

  const onSearch = searchVal => {
    setFilterOptionsSelected(prev => ({ ...prev, searchKey: searchVal }));
  };

  useEffect(() => {
    const newStudentsInfo = getFilteredStudentTransferInfo({
      studentList: initalTransferStudentList.current,
      filterOptions: filterOptionsSelected,
    });

    if (newStudentsInfo.length) {
      setTransferStudentList(newStudentsInfo);
    }
  }, [filterOptionsSelected]);

  useEffect(() => {
    const slotSelectorAddedStudentList = getSlotSelectorAddedList(transferStudentAPIData);

    initalTransferStudentList.current = slotSelectorAddedStudentList;
    setTransferStudentList(slotSelectorAddedStudentList);
  }, [transferStudentAPIData]);

  const warningMessage = transferReason && reasonBasedWarningMessage(transferReason);
  const disableButton =
    !transferReason ||
    !transferStudentList.some(
      studentData => studentData.slot_details && studentData.selected,
    );

  const tableColumnData = [
    {
      title: 'Student name',
    },
    {
      title: 'Parent name',
    },
    { title: 'Grade' },
    {
      title: 'Region',
      showFilters: true,
      filterApplied: filterOptionsSelected.regionFilter.length,
      filterComponent: (
        <FilterDropdown
          clearable
          fluid
          multiple
          search
          selection
          value={filterOptionsSelected.regionFilter}
          onChange={onRegionFilter}
          options={semanticDropdownDataFormat(REGION_FILTER_OPTION)}
        />
      ),
    },
    { title: 'Class balance' },
    { title: 'Tenure end date' },
    { title: 'Transfer Status' },
    { title: 'Regular slots', showFilters: true },
    {
      checkSelectData: {
        checkbox: true,
        checked: selectAllStudents,
        onSelection: onSelectAllStudents,
      },
    },
  ];

  return (
    <TransferTableRoot>
      <Row alignItems="center" justifyContent="space-between">
        <HeaderCard nowrap alignItems="baseline">
          <Text variant="h6">Transfer Students List</Text>
          <Text variant="body3">(Only Active or Suspended students)</Text>
        </HeaderCard>
        <Search showNewSearchBar={true} onSearch={onSearch} placeholder="Search" />
      </Row>

      <TableContainer>
        <Filters filters={[]} handleFilterChange={() => {}} />
        <Table
          headerCell={tableColumnData}
          dataLength={transferStudentList.length}
          showInfo={false}
          scrollBodyHeight="480px"
        >
          {transferStudentList.map(studentData => {
            const {
              student_id: id,
              name: studentName,
              parent_name: parentName,
              grade,
              region,
              transfer_status: transferStatus,
              selected,
              slot_details: regularSlots,
              primary_course: primaryCourseDetails,
            } = studentData || {};

            const {
              classes_balance: classesBalance,
              validity_till_date: validityTillDate,
            } = primaryCourseDetails || {};
            const tenureEndDate = getDateOfRequest(validityTillDate);

            const slotHoverTriggered = hoverCheckSlot === id && !regularSlots;
            const slotTime = convertEpochDateToSTSlotTime();
            const reasonForTransfer = TRANSFER_REASONS[transferReason];
            const cardType = regularSlots ? transferStatus || 'NOT_STARTED' : 'INITIATED';
            const drawerData = {
              cardType,
              studentName,
              grade,
              region,
              classBalance: classesBalance,
              tenureEndDate: validityTillDate,
              reasonForTransfer,
              studentId: id,
            };

            return (
              <TableRow key={id}>
                <StyledTableCell
                  width={2}
                  onClick={() => {
                    if (!id) return () => {};
                    return window.open(`/students/${id}`, '_blank');
                  }}
                >
                  {studentName || '-'}
                </StyledTableCell>
                <TableCell width={2}>{parentName || '-'}</TableCell>
                <TableCell>{grade || '-'}</TableCell>
                <TableCell>{region}</TableCell>
                <TableCell>{classesBalance || '-'}</TableCell>
                <TableCell>{tenureEndDate || '-'}</TableCell>
                <TableCell>
                  <TransferStatusCards
                    cardType={regularSlots ? transferStatus : 'INITIATED'}
                  />
                </TableCell>
                <TableCell>
                  {regularSlots ? (
                    slotTime
                  ) : (
                    <EditSlotWrapper alignItems="center">
                      <EditSlotIcon onClick={() => onEditTransferSlots(drawerData)} />
                      <span>-</span>
                    </EditSlotWrapper>
                  )}
                </TableCell>
                <StyledTableCell>
                  <CheckBoxWrapper
                    disabled={!regularSlots}
                    justifyContent="center"
                    onClick={regularSlots && (() => onStudentSelection(id))}
                    onMouseEnter={() => setHoveredCheckSlot(id)}
                    onMouseLeave={() => setHoveredCheckSlot('')}
                  >
                    <Checkbox checked={selected} disabled={!regularSlots} />
                    {slotHoverTriggered && (
                      <ArrowTooltip variant="body3" color={COLORS.WHITE}>
                        Edit slots first
                      </ArrowTooltip>
                    )}
                  </CheckBoxWrapper>
                </StyledTableCell>
              </TableRow>
            );
          })}
        </Table>
      </TableContainer>

      <TransferListFooter justifyContent="flex-end" alignItems="center">
        <DropdownWrapper>
          <Dropdown
            options={BULK_TRANSFER_REASONS_OPTIONS}
            value={transferReason}
            onChange={option => setTransferReason(option)}
            placeholder="Reason for transfer"
            type="primary"
            height="max-content"
            restrictSearch
          />
          <DropdonwWarningMessage variant="body3">
            {warningMessage}
          </DropdonwWarningMessage>
        </DropdownWrapper>
        <Button
          label="Find and assign teacher for selected students"
          size="small"
          isDisabled={disableButton}
        />
      </TransferListFooter>
    </TransferTableRoot>
  );
};

export default TransferStudentList;
